import {
  type BhkConfig,
  type ListingStatus,
  type PropertyCategory,
  type PropertySort,
  type PropertyType,
  type TransactionType,
} from '@resolute/schema';

/**
 * Client-side representation of the search state. The URL query string is the
 * single source of truth (shareable, bookmarkable, SSR-friendly); these pure
 * functions translate between it and this typed shape, and into API params.
 */
export interface SearchState {
  q?: string;
  transaction?: TransactionType;
  category?: PropertyCategory;
  type: PropertyType[];
  config: BhkConfig[];
  listingStatus: ListingStatus[];
  city?: string;
  locality?: string;
  state?: string;
  areas: string[];
  /** Single-select — CPzone's developer list runs into the thousands, so
   *  this is a dropdown (like city/state), not a checkbox multi-select. */
  developer?: string;
  priceMin?: number;
  priceMax?: number;
  areaMin?: number;
  areaMax?: number;
  possessionStart?: string;
  possessionEnd?: string;
  amenities: string[];
  verified: boolean;
  preLeased: boolean;
  giftCity: boolean;
  sort: PropertySort;
  page: number;
}

export const DEFAULT_SEARCH_STATE: SearchState = {
  type: [],
  config: [],
  listingStatus: [],
  areas: [],
  amenities: [],
  verified: false,
  preLeased: false,
  giftCity: false,
  sort: 'relevance',
  page: 1,
};

const CSV_KEYS = ['type', 'config', 'listingStatus', 'areas', 'amenities'] as const;
const BOOL_KEYS = ['verified', 'preLeased', 'giftCity'] as const;
const NUM_KEYS = ['priceMin', 'priceMax', 'areaMin', 'areaMax'] as const;
const DATE_KEYS = ['possessionStart', 'possessionEnd'] as const;

/** Parse a URLSearchParams into a typed SearchState (with defaults). */
export function parseSearchParams(params: URLSearchParams): SearchState {
  const csv = (key: string): string[] => {
    const raw = params.get(key);
    return raw
      ? raw
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
  };
  const num = (key: string): number | undefined => {
    const raw = params.get(key);
    if (raw == null || raw === '') return undefined;
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
  };

  const page = num('page');

  return {
    q: params.get('q') || undefined,
    transaction: (params.get('transaction') as TransactionType) || undefined,
    category: (params.get('category') as PropertyCategory) || undefined,
    type: csv('type') as PropertyType[],
    config: csv('config') as BhkConfig[],
    listingStatus: csv('listingStatus') as ListingStatus[],
    city: params.get('city') || undefined,
    locality: params.get('locality') || undefined,
    state: params.get('state') || undefined,
    areas: csv('areas'),
    developer: params.get('developer') || undefined,
    priceMin: num('priceMin'),
    priceMax: num('priceMax'),
    areaMin: num('areaMin'),
    areaMax: num('areaMax'),
    possessionStart: params.get('possessionStart') || undefined,
    possessionEnd: params.get('possessionEnd') || undefined,
    amenities: csv('amenities'),
    verified: params.get('verified') === 'true',
    preLeased: params.get('preLeased') === 'true',
    giftCity: params.get('giftCity') === 'true',
    sort: (params.get('sort') as PropertySort) || 'relevance',
    page: page && page > 0 ? page : 1,
  };
}

/** Serialise a SearchState back into URLSearchParams, omitting defaults. */
export function serializeSearchState(state: SearchState): URLSearchParams {
  const params = new URLSearchParams();
  if (state.q) params.set('q', state.q);
  if (state.transaction) params.set('transaction', state.transaction);
  if (state.category) params.set('category', state.category);
  for (const key of CSV_KEYS) {
    const value = state[key];
    if (value.length) params.set(key, value.join(','));
  }
  if (state.city) params.set('city', state.city);
  if (state.locality) params.set('locality', state.locality);
  if (state.state) params.set('state', state.state);
  if (state.developer) params.set('developer', state.developer);
  for (const key of NUM_KEYS) {
    const value = state[key];
    if (value != null) params.set(key, String(value));
  }
  for (const key of DATE_KEYS) {
    const value = state[key];
    if (value) params.set(key, value);
  }
  for (const key of BOOL_KEYS) {
    if (state[key]) params.set(key, 'true');
  }
  if (state.sort !== 'relevance') params.set('sort', state.sort);
  if (state.page > 1) params.set('page', String(state.page));
  return params;
}

/** Map a SearchState into the params object for the API request. */
export function toApiParams(state: SearchState): Record<string, string | number> {
  const params: Record<string, string | number> = { sort: state.sort, page: state.page };
  if (state.q) params.q = state.q;
  if (state.transaction) params.transaction = state.transaction;
  if (state.category) params.category = state.category;
  for (const key of CSV_KEYS) {
    if (state[key].length) params[key] = state[key].join(',');
  }
  if (state.city) params.city = state.city;
  if (state.locality) params.locality = state.locality;
  if (state.state) params.state = state.state;
  if (state.developer) params.developer = state.developer;
  for (const key of NUM_KEYS) {
    const value = state[key];
    if (value != null) params[key] = value;
  }
  for (const key of DATE_KEYS) {
    const value = state[key];
    if (value) params[key] = value;
  }
  for (const key of BOOL_KEYS) {
    if (state[key]) params[key] = 'true';
  }
  return params;
}

/** Count of user-applied filters (excludes sort/page/q) for UI badges. */
export function countActiveFilters(state: SearchState): number {
  let n = 0;
  n +=
    state.type.length +
    state.config.length +
    state.listingStatus.length +
    state.areas.length +
    state.amenities.length;
  if (state.category) n += 1;
  if (state.transaction) n += 1;
  if (state.city) n += 1;
  if (state.locality) n += 1;
  if (state.state) n += 1;
  if (state.developer) n += 1;
  if (state.priceMin != null) n += 1;
  if (state.priceMax != null) n += 1;
  if (state.areaMin != null) n += 1;
  if (state.areaMax != null) n += 1;
  if (state.possessionStart) n += 1;
  if (state.possessionEnd) n += 1;
  if (state.verified) n += 1;
  if (state.preLeased) n += 1;
  if (state.giftCity) n += 1;
  return n;
}
